import { Component, type ErrorInfo, type ReactNode } from 'react';
import PageContentFallback from './layout/PageContentFallback';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  isChunkError: boolean;
  /** Чанк устарел после деплоя — тихо перезагружаем, без экрана ошибки */
  silentReload: boolean;
}

function isChunkLoadError(error: unknown): boolean {
  const message = error instanceof Error ? error.message : String(error);
  return (
    message.includes('Failed to fetch dynamically imported module') ||
    message.includes('Loading chunk') ||
    message.includes('Importing a module script failed') ||
    message.includes('error loading dynamically imported module')
  );
}

export default class ErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false, isChunkError: false, silentReload: false };

  componentDidMount() {
    // После успешной загрузки сбрасываем флаг авто-reload (иначе второй деплой не сработает)
    try {
      sessionStorage.removeItem('chunk_reload');
    } catch {
      /* ignore */
    }
  }

  static getDerivedStateFromError(error: unknown): State {
    const isChunkError = isChunkLoadError(error);
    let silentReload = false;
    if (isChunkError) {
      try {
        silentReload = !sessionStorage.getItem('chunk_reload');
      } catch {
        silentReload = true;
      }
    }
    return { hasError: true, isChunkError, silentReload };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('App error:', error, info.componentStack);
    if (this.state.silentReload) {
      try {
        sessionStorage.setItem('chunk_reload', '1');
      } catch {
        /* ignore */
      }
      window.location.reload();
    }
  }

  componentDidUpdate(_: Props, prevState: State) {
    if (prevState.hasError && !this.state.hasError) {
      try {
        sessionStorage.removeItem('chunk_reload');
      } catch {
        /* ignore */
      }
    }
  }

  handleRetry = () => {
    try {
      sessionStorage.removeItem('chunk_reload');
    } catch {
      /* ignore */
    }
    this.setState({ hasError: false, isChunkError: false, silentReload: false });
    window.location.reload();
  };

  render() {
    if (!this.state.hasError) {
      return this.props.children;
    }

    // Устаревший lazy-чанк после деплоя: показываем скелетон, не «Не удалось загрузить»
    if (this.state.silentReload) {
      return (
        <div className="min-h-[50vh] bg-white">
          <PageContentFallback />
        </div>
      );
    }

    return (
      <div className="min-h-screen bg-white flex items-center justify-center px-4">
        <div className="max-w-md text-center">
          <h1 className="text-xl font-bold mb-2">Не удалось загрузить страницу</h1>
          <p className="text-gray-600 mb-6">
            {this.state.isChunkError
              ? 'Обновите страницу — возможно, вышло обновление сайта.'
              : 'Произошла ошибка. Попробуйте обновить страницу.'}
          </p>
          <button
            type="button"
            onClick={this.handleRetry}
            className="bg-red-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-red-700"
          >
            Обновить
          </button>
        </div>
      </div>
    );
  }
}
